import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "/logo.png";

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-white hover:bg-white/20">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <img src={LOGO_URL} alt="My Wishlists" className="h-12" />
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-8 h-8 text-purple-600" />
            <h1 className="text-4xl font-bold text-gray-800">Terms & Conditions</h1>
          </div>
          
          <p className="text-sm text-gray-500 mb-8">Last Updated: December 3, 2024</p>

          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing and using My Wishlists ("the Service"), you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you may not use our service. These terms comply with the Indian Contract Act, 1872 and the Consumer Protection Act, 2019.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">2. Service Description</h2>
              <p className="mb-3">
                My Wishlists is a platform that allows users to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Create and manage events and wishlists</li>
                <li>Share wishlists with friends and family</li>
                <li>Select gifts from others' wishlists</li>
                <li>Create and share digital invitations</li>
                <li>Collaborate on wishlists with multiple users</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">3. User Accounts and Authentication</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.1 Account Creation</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>You must be at least 18 years old to use this service</li>
                <li>Authentication is provided via Google OAuth</li>
                <li>You are responsible for maintaining the confidentiality of your account</li>
                <li>You agree to provide accurate and complete information</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">3.2 Account Security</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>You are responsible for all activities under your account</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>We reserve the right to suspend or terminate accounts that violate these terms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">4. User Conduct and Responsibilities</h2>
              <p className="mb-3">You agree NOT to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the service for any illegal or unauthorized purpose</li>
                <li>Violate any laws in your jurisdiction</li>
                <li>Infringe upon the rights of others</li>
                <li>Upload malicious code, viruses, or harmful content</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Impersonate any person or entity</li>
                <li>Scrape or extract data from the service without permission</li>
                <li>Interfere with or disrupt the service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">5. Content and Intellectual Property</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">5.1 Your Content</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>You retain ownership of content you upload (wishlist items, images, etc.)</li>
                <li>You grant us a license to use, store, and display your content to provide the service</li>
                <li>You are responsible for ensuring you have rights to any content you upload</li>
                <li>You must not upload copyrighted material without permission</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">5.2 Our Content</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>The service, including design, text, graphics, and code, is owned by My Wishlists</li>
                <li>You may not copy, modify, or distribute our content without permission</li>
                <li>Our trademarks and branding may not be used without authorization</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">6. Third-Party Links and Services</h2>
              <p>
                Our service may contain links to third-party websites (e.g., Amazon, Myntra, Nykaa). We are not responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>The content or practices of third-party websites</li>
                <li>Transactions between you and third parties</li>
                <li>Product quality, pricing, or availability from external retailers</li>
                <li>Any disputes arising from third-party purchases</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">7. Disclaimers and Limitations</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">7.1 Service "As Is"</h3>
              <p className="mb-3">
                The service is provided on an "as is" and "as available" basis without warranties of any kind, either express or implied.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">7.2 No Guarantee</h3>
              <p className="mb-3">We do not guarantee that:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>The service will be uninterrupted or error-free</li>
                <li>Product information fetched from external sites will be accurate</li>
                <li>All features will work perfectly at all times</li>
                <li>Data loss will never occur</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">7.3 Limitation of Liability</h3>
              <p>
                To the maximum extent permitted by Indian law, My Wishlists shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">8. Pricing and Payments</h2>
              <p className="mb-3">
                Currently, My Wishlists is offered free of charge. We reserve the right to introduce paid features or subscriptions in the future with advance notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">9. Data and Privacy</h2>
              <p>
                Your use of the service is also governed by our <button onClick={() => navigate('/privacy')} className="text-purple-600 hover:underline font-semibold">Privacy Policy</button>. Please review it to understand how we collect, use, and protect your data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">10. Termination</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">10.1 By You</h3>
              <p className="mb-3">
                You may stop using the service and delete your account at any time.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">10.2 By Us</h3>
              <p className="mb-3">We may suspend or terminate your account if:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You violate these Terms and Conditions</li>
                <li>You engage in fraudulent or illegal activity</li>
                <li>We are required to do so by law</li>
                <li>We discontinue the service</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">11. Modifications to Service and Terms</h2>
              <p className="mb-3">
                We reserve the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Modify or discontinue the service at any time</li>
                <li>Update these Terms and Conditions</li>
                <li>Change features or functionality</li>
              </ul>
              <p className="mt-3">
                We will notify users of significant changes. Continued use after changes constitutes acceptance.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">12. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless My Wishlists, its officers, directors, and employees from any claims, damages, or expenses arising from your use of the service or violation of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">13. Dispute Resolution</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">13.1 Informal Resolution</h3>
              <p className="mb-3">
                If you have a dispute, please contact us first at support@mywishlists.in to attempt an informal resolution.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">13.2 Arbitration</h3>
              <p className="mb-3">
                If informal resolution fails, disputes shall be resolved through arbitration in accordance with the Arbitration and Conciliation Act, 1996.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-2 mt-4">13.3 Consumer Rights</h3>
              <p>
                Nothing in these terms limits your rights as a consumer under the Consumer Protection Act, 2019.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">14. Governing Law and Jurisdiction</h2>
              <p>
                These Terms and Conditions are governed by the laws of India. Any legal action or proceeding shall be subject to the exclusive jurisdiction of the courts in Bangalore, India.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">15. Severability</h2>
              <p>
                If any provision of these terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-3">16. Contact Information</h2>
              <p className="mb-3">
                For questions about these Terms and Conditions, contact us:
              </p>
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="font-semibold">My Wishlists</p>
                <p>Email: support@mywishlists.in</p>
                <p>Address: 201-A, NKEA, Sarjapur Road, Bangalore, India</p>
              </div>
            </section>

            <section className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
              <p className="font-semibold text-gray-800 mb-2">Acknowledgment:</p>
              <p className="text-sm">
                By using My Wishlists, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
